import { useCallback } from 'react';
import { useSocialShareModal } from 'containers/SocialShareModal/SocialShareModalContext';

/**
 * Shared hook for "share now vs schedule" orchestration. Reads scheduledDate and
 * onSchedulePost from modal context; each platform only provides how to share and
 * how to schedule (shareNow / schedule).
 */
export default function useOnShare<TArgs extends unknown[]>(fns: {
  shareNow: (...args: TArgs) => void | Promise<void>;
  schedule: (scheduledDate: number, ...args: TArgs) => void | Promise<void>;
}): (...args: TArgs) => Promise<void> {
  const { scheduledDate, schedulePost } = useSocialShareModal();
  const { shareNow, schedule } = fns;

  return useCallback(
    async (...args: TArgs): Promise<void> => {
      if (!scheduledDate) {
        await shareNow(...args);

        return;
      }

      await schedulePost(() =>
        Promise.resolve(schedule(scheduledDate, ...args)),
      );
    },
    [scheduledDate, schedulePost, shareNow, schedule],
  );
}
